/**
 * Custom PDP Ingredients Section
 * Pop-Art Animationen mit GSAP und interaktive Karten
 */

class CustomPDPIngredients extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.carousel = this.querySelector('.ingredients-carousel__wrapper');
    this.cards = this.querySelectorAll('.ingredient-card-mobile');
    this.dots = this.querySelectorAll('.carousel-dot');
    this.currentSlide = 0;
    this.expandedCard = null;
    
    this.init();
  }

  init() {
    console.log('CustomPDPIngredients init started');
    
    // Warte auf GSAP
    this.waitForGSAP().then(() => {
      console.log('GSAP loaded, starting animations');
      this.initGSAPAnimations();
      this.initCarousel();
      this.initCardInteractions();
      this.setupIntersectionObserver();
    }).catch((error) => {
      console.error('GSAP loading failed:', error);
      // Fallback ohne GSAP
      this.initFallbackAnimations();
    });
  }

  waitForGSAP() {
    return new Promise((resolve, reject) => {
      if (typeof gsap !== 'undefined') {
        console.log('GSAP found immediately');
        resolve();
      } else {
        console.log('Waiting for GSAP...');
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max
        
        const checkGSAP = setInterval(() => {
          attempts++;
          if (typeof gsap !== 'undefined') {
            console.log('GSAP loaded after', attempts * 100, 'ms');
            clearInterval(checkGSAP);
            resolve();
          } else if (attempts >= maxAttempts) {
            console.error('GSAP not found after', maxAttempts * 100, 'ms');
            clearInterval(checkGSAP);
            reject(new Error('GSAP timeout'));
          }
        }, 100);
      }
    });
  }

  initGSAPAnimations() {
    // Register ScrollTrigger if available
    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }

    // Pop-Art Entrance Animations
    this.animateHeroEntry();
    this.animateSpeechBubbles();
    this.animateOverlay();
    this.animateCards();
  }

  animateHeroEntry() {
    const heroImage = this.querySelector('.custom-pdp-ingredients__hero-image-mobile');
    if (!heroImage) return;

    gsap.fromTo(heroImage, 
      {
        scale: 0.8,
        opacity: 0,
        rotation: -5
      },
      {
        scale: 1,
        opacity: 1,
        rotation: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: heroImage,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      }
    );
  }

  animateSpeechBubbles() {
    const bubbles = this.querySelectorAll('.speech-bubble');
    console.log('Found speech bubbles:', bubbles.length);
    
    // Set initial state (hidden)
    bubbles.forEach(bubble => {
      const isLeft = bubble.classList.contains('speech-bubble--left');
      const bubbleText = bubble.querySelector('.speech-bubble__text');
      
      gsap.set(bubble, {
        scale: 0,
        rotation: isLeft ? -15 : 15,
        opacity: 0,
        x: isLeft ? -50 : 50
      });
      
      if (bubbleText) {
        gsap.set(bubbleText, {
          scale: 0,
          opacity: 0,
          y: 10
        });
      }
    });
    
    // Store bubbles for later animation
    this.speechBubbles = bubbles;
  }

  // Method to start bubble animations when section is visible
  startBubbleAnimations() {
    if (!this.speechBubbles) return;
    
    console.log('Starting speech bubble animations');
    
    this.speechBubbles.forEach((bubble, index) => {
      const isLeft = bubble.classList.contains('speech-bubble--left');
      const bubbleText = bubble.querySelector('.speech-bubble__text');
      
      console.log(`Animating bubble ${index}:`, { isLeft, hasText: !!bubbleText });
      
      // Animate bubble
      gsap.to(bubble, {
        scale: 1,
        rotation: isLeft ? -5 : 5,
        opacity: 1,
        x: 0,
        duration: 0.8,
        delay: 0.2 + (index * 0.3), // Nacheinander mit 0.3s Abstand
        ease: "elastic.out(1, 0.5)",
        onComplete: () => console.log(`Bubble ${index} animation complete`)
      });

      // Text Animation (wenn vorhanden) - nach der Sprechblase
      if (bubbleText) {
        gsap.to(bubbleText, {
          scale: 1,
          opacity: 1,
          y: 0,
          duration: 0.5,
          delay: 0.6 + (index * 0.3) + 0.2, // Nach der Sprechblase
          ease: "back.out(1.7)",
          onComplete: () => console.log(`Text ${index} animation complete`)
        });
      }

      // Pop-Art Hover Effect
      bubble.addEventListener('mouseenter', () => {
        gsap.to(bubble, {
          scale: 1.1,
          rotation: isLeft ? -8 : 8,
          duration: 0.3,
          ease: "power2.out"
        });
      });

      bubble.addEventListener('mouseleave', () => {
        gsap.to(bubble, {
          scale: 1,
          rotation: isLeft ? -5 : 5,
          duration: 0.3,
          ease: "power2.out"
        });
      });
    });
  }

  initFallbackAnimations() {
    console.log('Using fallback CSS animations');
    const bubbles = this.querySelectorAll('.speech-bubble');
    
    bubbles.forEach((bubble, index) => {
      setTimeout(() => {
        bubble.style.animation = `popIn 0.8s ease-out forwards`;
        bubble.style.animationDelay = `${index * 0.3}s`;
      }, 500);
    });
  }

  animateOverlay() {
    const overlay = this.querySelector('.hero-content-overlay');
    if (!overlay) return;

    gsap.fromTo(overlay,
      {
        y: 50,
        opacity: 0,
        backdropFilter: 'blur(0px)'
      },
      {
        y: 0,
        opacity: 1,
        backdropFilter: 'blur(15px)',
        duration: 1,
        delay: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: overlay,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      }
    );
  }

  animateCards() {
    this.cards.forEach((card, index) => {
      gsap.fromTo(card,
        {
          y: 30,
          opacity: 0,
          scale: 0.9
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.6,
          delay: index * 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });
  }

  initCarousel() {
    if (!this.carousel) return;

    // Touch/Swipe functionality
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.carousel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    });

    this.carousel.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    });

    this.carousel.addEventListener('touchend', () => {
      if (!isDragging) return;
      
      const diff = startX - currentX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && this.currentSlide < this.cards.length - 1) {
          this.goToSlide(this.currentSlide + 1);
        } else if (diff < 0 && this.currentSlide > 0) {
          this.goToSlide(this.currentSlide - 1);
        }
      }

      isDragging = false;
    });

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    // Auto-update active dot on scroll
    this.carousel.addEventListener('scroll', () => {
      this.updateActiveDot();
    });
  }

  initCardInteractions() {
    this.cards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleCard(card, index);
      });
    });
  }

  toggleCard(card, index) {
    const isExpanded = card.classList.contains('expanded');
    
    if (isExpanded) {
      this.collapseCard(card);
    } else {
      // Collapse other cards first
      if (this.expandedCard && this.expandedCard !== card) {
        this.collapseCard(this.expandedCard);
      }
      this.expandCard(card, index);
    }
  }

  expandCard(card, index) {
    card.classList.add('expanded');
    this.expandedCard = card;

    // GSAP Pop-Art expansion animation
    gsap.to(card, {
      duration: 0.5,
      ease: "power2.out",
      onStart: () => {
        // Scroll card into view
        this.scrollToCard(index);
      }
    });

    // Animate card content
    const backContent = card.querySelector('.ingredient-card__back');
    gsap.fromTo(backContent.children,
      {
        y: 20,
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.4,
        delay: 0.2,
        stagger: 0.1,
        ease: "power2.out"
      }
    );

    // Pop-Art scale effect
    gsap.fromTo(card,
      { scale: 1 },
      { 
        scale: 1.05,
        duration: 0.2,
        ease: "power2.out",
        yoyo: true,
        repeat: 1
      }
    );
  }

  collapseCard(card) {
    card.classList.remove('expanded');
    if (this.expandedCard === card) {
      this.expandedCard = null;
    }

    // GSAP collapse animation
    gsap.to(card, {
      duration: 0.4,
      ease: "power2.out"
    });
  }

  scrollToCard(index) {
    const card = this.cards[index];
    if (!card) return;
    
    const cardRect = card.getBoundingClientRect();
    const carouselRect = this.carousel.getBoundingClientRect();
    const scrollLeft = this.carousel.scrollLeft;
    
    // Calculate scroll position to center the expanded card
    const cardCenter = cardRect.left - carouselRect.left + scrollLeft + (cardRect.width / 2);
    const carouselCenter = this.carousel.offsetWidth / 2;
    const targetScroll = cardCenter - carouselCenter;
    
    // Use GSAP if available, otherwise smooth scroll
    if (typeof gsap !== 'undefined') {
      gsap.to(this.carousel, {
        scrollLeft: Math.max(0, targetScroll),
        duration: 0.6,
        ease: "power2.out"
      });
    } else {
      this.carousel.scrollTo({
        left: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
    }
  }

  goToSlide(slideIndex) {
    if (slideIndex < 0 || slideIndex >= this.cards.length) return;
    
    this.currentSlide = slideIndex;
    this.scrollToCard(slideIndex);
    this.updateActiveDot();
  }

  updateActiveDot() {
    const scrollLeft = this.carousel.scrollLeft;
    const cardWidth = 150 + 16;
    const activeIndex = Math.round(scrollLeft / cardWidth);
    
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
    
    this.currentSlide = activeIndex;
  }

  setupIntersectionObserver() {
    // Animation when section comes into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log('Section is visible, starting animations');
          this.playEntranceAnimations();
          observer.unobserve(entry.target);
        }
      });
    }, { 
      threshold: 0.3, // Section muss 30% sichtbar sein
      rootMargin: '0px 0px -100px 0px' // Start etwas früher
    });

    observer.observe(this);
  }

  playEntranceAnimations() {
    console.log('Playing entrance animations');
    
    // Start with speech bubbles
    this.startBubbleAnimations();
    
    // Then hero image
    this.animateHeroEntry();
    
    // Then overlay
    setTimeout(() => {
      this.animateOverlay();
    }, 1000);
    
    // Finally cards and dots
    setTimeout(() => {
      this.animateCards();
      
      if (this.dots && this.dots.length > 0) {
        gsap.fromTo(this.dots,
          {
            scale: 0,
            opacity: 0
          },
          {
            scale: 1,
            opacity: 1,
            duration: 0.4,
            delay: 0.3,
            stagger: 0.1,
            ease: "elastic.out(1, 0.5)"
          }
        );
      }
    }, 1500);
  }
}

// Simple CSS Animation System (fallback)
function initSimpleAnimations() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const target = entry.target;
        const delay = target.dataset.animationDelay || '0s';
        
        setTimeout(() => {
          target.classList.add('visible');
        }, parseFloat(delay) * 1000);
      }
    });
  }, {
    threshold: 0.3,
    rootMargin: '0px 0px -50px 0px'
  });
  
  // Observe all animation elements
  document.querySelectorAll('.animate-on-scroll, .animate-text').forEach(el => {
    observer.observe(el);
  });
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initIngredients();
    initSimpleAnimations();
  });
} else {
  initIngredients();
  initSimpleAnimations();
}

function initIngredients() {
  // Wait a bit for other scripts to load
  setTimeout(() => {
    const sections = document.querySelectorAll('.custom-pdp-ingredients');
    
    sections.forEach((section, index) => {
      if (!section.customInstance) {
        section.customInstance = new CustomPDPIngredients.call(section);
      }
    });
  }, 100);
}

// Re-initialize on theme editor updates
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('custom-pdp-ingredients')) {
    initIngredients();
  }
});

export { CustomPDPIngredients };
